import React, {
  useMemo,
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import { AnimatePresence } from '@sopranodesign/framer-motion';
import {
  Flex,
  Span,
  Modal,
  Table,
  Button,
  Checkbox,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  TextInput,
  PanelSlider,
  ModalPortal,
  TableActions,
  useNotification,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import { useQuery, useQueryClient } from '@sopranodesign/pulse-hooks';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';

import {
  fetchZendeskTickets,
  updateZendeskTicketFilterName,
  recrawlZendeskTicket,
  fetchZendeskTicketTags,
  saveZendeskTicketTags,
  createZendeskTicketsTag,
  fetchZendeskTicketsAvailableTags
} from '../api';
import { StateLevel } from '.';
import WarningMessageBody from './WarningMessageBody';
import { useTagManager } from './useTagManager';
import { useBulkSelection } from './useBulkSelection';
import { runBulkAction, describeBulkResult } from './bulkActionUtils';
import BulkActionsBar from './BulkActionsBar';
import TagModal from './TagModal';

const ZendeskTicketsList = ({
  filterId,
  handleHide,
  onOpenTagsModal,
  onOpenRecrawlModal,
  loadingIds,
  errorIds
}) => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');
  const canEditTag = hasPermission('edit generative ai tag');
  const canEditResource = hasPermission('edit generative ai resource') && hasPermission('edit generative ai zendesk tickets');
  const canBulkSelect = canEditResource || (canViewTag && canEditTag);

  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  const [shouldRefetch, setShouldRefetch] = useState(false);

  const bulk = useBulkSelection();
  const [bulkModal, setBulkModal] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [isBulkTagsModalVisible, setIsBulkTagsModalVisible] = useState(false);

  const { data: availableTagsData } = useQuery({
    queryKey: ['generative-ai/zendesk/tickets/groups/available'],
    queryFn: fetchZendeskTicketsAvailableTags,
    staleTime: 5 * 60 * 1000,
    enabled: canViewTag
  });

  const { tags, selectedTags, setSelectedTags, handleTagChange, setTags } = useTagManager({
    scope: 'generative_ai_zendesk_tickets',
    createTagFn: createZendeskTicketsTag
  });

  const order = useOrderableTableReducer({ id: `zendesk-tickets-${filterId}` });
  const pagination = usePaginationReducer({ id: `zendesk-tickets-${filterId}` });

  const { data, isLoading } = useQuery({
    queryKey: [
      'generative-ai/zendesk/tickets',
      filterId,
      { ...pagination.params, ...order.params }
    ],
    queryFn: () => fetchZendeskTickets(filterId, { ...pagination.params, ...order.params }),
    keepPreviousData: true,
    refetchInterval: shouldRefetch ? 5000 : false
  });

  const response = useMemo(() => data?.data ?? {}, [data]);
  const tickets = useMemo(() => response.data ?? [], [response]);
  const meta = response.meta ?? {};
  const filterName = response.filter_name ?? 'Tickets';
  const pageIds = useMemo(() => tickets.map(ticket => ticket.id), [tickets]);

  const globalHasPending = response.has_pending ?? false;

  useEffect(() => {
    setShouldRefetch(globalHasPending);
  }, [globalHasPending]);

  const findTicket = (id) => tickets.find(ticket => ticket.id === id);

  const refetchTickets = () => {
    queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/tickets', filterId] });
  };

  const handleBulkRecrawl = () => {
    const ids = bulk.selectedIds;
    if (ids.length === 0) {
      return;
    }
    setBulkModal({
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl ${ids.length} selected ticket${ids.length > 1 ? 's' : ''}?`}
          contentBody="This will update their content."
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        setBulkLoading(true);
        try {
          const result = await runBulkAction(ids, recrawlZendeskTicket);
          if (result.succeeded.length > 0) {
            showSuccess(`${describeBulkResult(result, 'recrawl requested', 'ticket')}.`);
          }
          if (result.failed.length > 0) {
            showError(`Failed to request recrawl for ${result.failed.length} ticket${result.failed.length > 1 ? 's' : ''}.`);
          }
          bulk.clear();
        } finally {
          setBulkLoading(false);
          setBulkModal(null);
          refetchTickets();
        }
      }
    });
  };

  const openBulkTagsModal = () => {
    const eligibleIds = bulk.selectedIds.filter(id => findTicket(id)?.status === 'Success');
    if (eligibleIds.length === 0) {
      showError('None of the selected tickets are ready to be tagged.');
      return;
    }
    const fetchedTags = availableTagsData?.data?.data ?? [];
    setTags(fetchedTags);
    setSelectedTags([]);
    setIsBulkTagsModalVisible(true);
  };

  const handleBulkTagConfirm = async () => {
    const eligibleIds = bulk.selectedIds.filter(id => findTicket(id)?.status === 'Success');
    setBulkLoading(true);
    try {
      const result = await runBulkAction(eligibleIds, async (id) => {
        const assignedResponse = await fetchZendeskTicketTags(id);
        const currentTagIds = (assignedResponse.data.data ?? []).map(tag => tag.id);
        const mergedTagIds = Array.from(new Set([...currentTagIds, ...selectedTags]));
        return saveZendeskTicketTags({ ticketId: id, groupIds: mergedTagIds });
      });
      if (result.succeeded.length > 0) {
        showSuccess(`${describeBulkResult(result, 'tagged', 'ticket')}.`);
      }
      if (result.failed.length > 0) {
        showError(`Failed to tag ${result.failed.length} ticket${result.failed.length > 1 ? 's' : ''}.`);
      }
      bulk.clear();
      refetchTickets();
    } finally {
      setBulkLoading(false);
      setIsBulkTagsModalVisible(false);
      setSelectedTags([]);
    }
  };

  const startEditingName = () => {
    setNameDraft(filterName);
    setIsEditingName(true);
  };

  const cancelEditingName = () => {
    setIsEditingName(false);
    setNameDraft('');
  };

  const saveFilterName = async () => {
    const name = nameDraft.trim();
    if (!name || name === filterName) {
      cancelEditingName();
      return;
    }

    setIsSavingName(true);
    try {
      await updateZendeskTicketFilterName(filterId, name);
      queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/tickets'] });
      showSuccess('Filter renamed successfully!');
      setIsEditingName(false);
    } catch (error) {
      showError(error.response?.data?.error ?? 'Failed to rename filter.');
    } finally {
      setIsSavingName(false);
    }
  };

  const panelHeader = isEditingName ? (
    <Flex center gap="sm">
      <TextInput
        autoFocus
        value={nameDraft}
        disabled={isSavingName}
        onChange={e => setNameDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { saveFilterName(); }
          if (e.key === 'Escape') { cancelEditingName(); }
        }}
      />
      <Button
        ml
        icon="check"
        loading={isSavingName}
        onClick={saveFilterName}
      >
      </Button>
      <Button
        ml
        icon="x"
        variant="secondary"
        colour="danger"
        disabled={isSavingName}
        onClick={cancelEditingName}
      >
      </Button>
    </Flex>
  ) : (
    <Flex center gap="sm">
      <Span>{filterName}</Span>
      <PermissionFilter can="edit generative ai resource">
        <PermissionFilter can="edit generative ai zendesk tickets">
          <Button ml icon="pencil" variant="secondary" onClick={startEditingName}></Button>
        </PermissionFilter>
      </PermissionFilter>
    </Flex>
  );

  return (
    <AnimatePresence>
      <PanelSlider
        header={panelHeader}
        handleHide={handleHide}
      >
        {isLoading && <LoadingContainer />}
        {!isLoading && !tickets.length && (
          <Paragraph mb secondary>No tickets available.</Paragraph>
        )}
        {!isLoading && tickets.length > 0 && (
          <Flex col>
            <PermissionFilter can="edit generative ai resource">
              <PermissionFilter can="edit generative ai zendesk tickets">
                <BulkActionsBar
                  count={bulk.count}
                  onClear={bulk.clear}
                  actions={[
                    <PermissionFilter can="view generative ai tag" key="bulk-tag">
                      <PermissionFilter can="edit generative ai tag">
                        <Button
                          icon="tag"
                          variant="secondary"
                          onClick={openBulkTagsModal}
                          mr
                        >
                          Tags
                        </Button>
                      </PermissionFilter>
                    </PermissionFilter>,
                    <Button
                      mr
                      key="bulk-recrawl"
                      icon="download"
                      variant="secondary"
                      loading={bulkLoading}
                      onClick={handleBulkRecrawl}
                    >
                      Recrawl
                    </Button>
                  ]}
                />
              </PermissionFilter>
            </PermissionFilter>
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      ...(canBulkSelect ? [{
                        label: (
                          <Checkbox
                            aria-label="Select all tickets"
                            checked={bulk.isAllSelected(pageIds)}
                            onChange={() => bulk.toggleAll(pageIds)}
                          />
                        )
                      }] : []),
                      { label: 'Subject', sort: 'subject' },
                      { label: 'Status', sort: 'status' },
                      { label: 'Crawled At', sort: 'updated_at' },
                      ...(canViewTag ? [{ label: 'Tags' }] : []),
                      ...(((canViewTag && canEditTag) || canEditResource) ? [null] : [])
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {tickets.map(ticket => (
                    <TableRow key={ticket.id}>
                      {canBulkSelect && (
                        <TableCell>
                          <Checkbox
                            aria-label={`Select ticket ${ticket.id}`}
                            checked={bulk.isSelected(ticket.id)}
                            onChange={() => bulk.toggle(ticket.id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>{ticket.subject}</TableCell>
                      <TableCell>
                        <Flex center>
                          <StateLevel level={ticket.status} />
                          <span>{ticket.status}</span>
                        </Flex>
                      </TableCell>
                      <TableCell>{ticket.updated_at}</TableCell>

                      <PermissionFilter can="view generative ai tag">
                        <TableCell>
                          {ticket.groups && ticket.groups.length > 0
                            ? ticket.groups.map(group => group.name).join(', ')
                            : 'No Tags'}
                        </TableCell>
                      </PermissionFilter>

                      {((canViewTag && canEditTag) || canEditResource) && (
                        <TableActions>
                          <PermissionFilter can="view generative ai tag">
                            <PermissionFilter can="edit generative ai tag">
                              {ticket.status === 'Success' && (
                                <Button
                                  icon="tag"
                                  onClick={() => onOpenTagsModal(ticket)}
                                >
                                  Tags
                                </Button>
                              )}
                            </PermissionFilter>
                          </PermissionFilter>

                          <PermissionFilter can="edit generative ai resource">
                            <PermissionFilter can="edit generative ai zendesk tickets">
                              <Button
                                icon="download"
                                loading={Boolean(loadingIds?.[ticket.id])}
                                disabled={globalHasPending}
                                onClick={() => onOpenRecrawlModal(ticket)}
                              >
                                Recrawl
                              </Button>
                            </PermissionFilter>
                          </PermissionFilter>

                          {errorIds?.[ticket.id] && (
                            <p style={{ color: 'red', marginTop: '4px' }}>
                              {errorIds[ticket.id]}
                            </p>
                          )}
                        </TableActions>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>

            {typeof meta.current_page === 'number' && (
              <Pagination pagination={meta} {...pagination.props} />
            )}

            <ModalPortal>
              <TagModal
                isVisible={isBulkTagsModalVisible}
                header={`Tag ${bulk.count} Tickets`}
                onCancel={() => {
                  setIsBulkTagsModalVisible(false);
                  setSelectedTags([]);
                }}
                onConfirm={handleBulkTagConfirm}
                tags={tags}
                selectedTags={selectedTags}
                handleTagChange={handleTagChange}
              />
            </ModalPortal>

            {bulkModal && (
              <ModalPortal>
                <Modal
                  header={bulkModal.header}
                  body={bulkModal.body}
                  confirmText={bulkModal.confirmText}
                  cancelText="Cancel"
                  handleCancel={() => setBulkModal(null)}
                  handleConfirm={bulkModal.handleConfirm}
                  loading={bulkLoading}
                />
              </ModalPortal>
            )}
          </Flex>
        )}
      </PanelSlider>
    </AnimatePresence>
  );
};

ZendeskTicketsList.propTypes = {
  filterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  handleHide: PropTypes.func.isRequired,
  onOpenTagsModal: PropTypes.func.isRequired,
  onOpenRecrawlModal: PropTypes.func.isRequired,
  loadingIds: PropTypes.object,
  errorIds: PropTypes.object.isRequired
};

export default ZendeskTicketsList;
